import type {
  LibraryDependency,
  LibraryItemView,
} from '@/lib/api/generated/model';
import { isInWorkspace } from './presence';

/**
 * What an installed workflow names that this workspace no longer holds.
 *
 * A workflow's prompts delegate to agents and skills by key. The service copies
 * those in when the workflow is installed, but nothing keeps them there: the
 * Library can take one back out, and the files page can delete its folder
 * without the Library ever hearing. Neither is prevented, and neither should
 * be — see spec 106's decision that a dependency is a warning rather than a bar.
 *
 * What is not acceptable is that nothing notices. The worker mirrors whatever
 * sits in `.skills/` into the sandbox and treats an absent folder as a silent
 * no-op, on purpose, so a run with a missing skill produces worse work and
 * reports nothing at all. This is the only place the gap can be seen.
 *
 * Empty for anything that is not an installed workflow: a workflow nobody has
 * installed is missing nothing, it simply has not been taken.
 */
export function missingParts(
  item: LibraryItemView,
  items: readonly LibraryItemView[],
): LibraryDependency[] {
  if (item.kind !== 'workflow' || !isInWorkspace(item.state)) return [];
  const byId = new Map(items.map((i) => [`${i.kind}/${i.key}`, i]));
  return (item.requires ?? []).filter((dep) => {
    const found = byId.get(`${dep.kind}/${dep.key}`);
    // Absent from the library counts too: the workflow names something nobody
    // can supply, which is worse than something merely uninstalled.
    return found === undefined || !isInWorkspace(found.state);
  });
}

/** The gap in words, for a badge and for a sentence (spec 107 FR-002). */
export function missingPartsLabel(count: number): string {
  return count === 1 ? '1 part missing' : `${String(count)} parts missing`;
}
